import { Box, Grid, Stack, Typography } from "@mui/material";
import StageLine from "components/stageLine";
import { useTemplate } from "containers/templateDetail/store";
import * as React from "react";
import SizeChart from "../sizeChart/chart";

export default function Material(): React.ReactElement {
  const { grams: templateGrams, app_description, attributes } = useTemplate();

  const keyValueAttrs = React.useMemo(() => {
    return attributes.filter((item) => item.type === "KEY_VALUE");
  }, [attributes]);

  const printCareAttrs = React.useMemo(() => {
    return attributes.filter((item) => item.type === "PRINT_CARE");
  }, [attributes]);

  const [printCareImages, printCareStr] = React.useMemo(() => {
    const imagesDOM = [];
    let str = "";

    for (let i = 0; i < printCareAttrs.length; i++) {
      const printCareAttr = printCareAttrs[i];
      if (str.length > 0) {
        str += "; ";
      }
      if (printCareAttr.key === "none-icon") {
        str += printCareAttr.value;
      } else {
        str +=
          printCareAttr.name +
          (printCareAttr.value ? ": " + printCareAttr.value : "");
        imagesDOM.push(
          <img
            key={i}
            src={`${process.env.NEXT_PUBLIC_SITE_URL}/resources/images/care-instructions/${printCareAttr.key}.svg`}
            style={{
              width: 30,
              height: 30,
            }}
            alt={printCareAttr.name || ""}
          />
        );
      }
    }

    return [imagesDOM, str];
  }, [attributes]);

  const sizeExpressionAttrs = React.useMemo(() => {
    return attributes.filter((item) => item.type === "SIZE_EXPRESSION");
  }, [attributes]);

  const templateWeightStr = React.useMemo(() => {
    return `${(templateGrams / 1000).toFixed(2)} KG`;
  }, [templateGrams]);

  return (
    <Grid container columnSpacing={4}>
      <Grid item xs={12} sm={12} md={6}>
        <Stack spacing={1}>
          <AttrValueItem name={"Product Weight"} value={templateWeightStr} />
          {keyValueAttrs.map((item, index) => (
            <AttrValueItem key={index} name={item.name} value={item.value} />
          ))}
        </Stack>
        <Box
          sx={{
            fontFamily: (theme) => theme.typography.fontFamily,
          }}
          dangerouslySetInnerHTML={{
            __html: app_description,
          }}
        />
        {printCareAttrs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" display="inline-block">
              Care instructions
            </Typography>
            <Stack spacing={2} direction="row" sx={{ my: 1 }}>
              {printCareImages}
            </Stack>
            <Typography variant="body2" display="inline-block">
              {printCareStr}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        {sizeExpressionAttrs.length > 0 && (
          <Box
            sx={{
              // maxWidth: { xs: "none", sm: 400 },
              ml: { xs: 0, md: "auto" },
              mt: { xs: 4, sm: 4, md: 0 },
              mb: 4,
            }}
          >
            {sizeExpressionAttrs.map((item, index) => (
              <React.Fragment key={index}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: (theme) => theme.typography.fontWeightBold,
                  }}
                >
                  {item.name}
                </Typography>
                <StageLine
                  steps={item.value.steps}
                  activeStep={item.value.activeStep}
                  stepLabels={item.value.stepLabels}
                />
              </React.Fragment>
            ))}
          </Box>
        )}

        <Box sx={{}}>
          <SizeChart />
          <Typography variant="caption" color="textSecondary">
            <b style={{ color: "red" }}>*</b>&nbsp;This data was obtained from
            manually measuring the product, it may be off by 1-2 CM.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

const AttrValueItem = ({ name, value }) => {
  return (
    <Box>
      <Typography variant="subtitle2" display="inline-block">
        {name}:
      </Typography>
      <Typography
        variant="body2"
        display="inline"
        color="textSecondary"
        sx={{ ml: 1 }}
      >
        {value}
      </Typography>
    </Box>
  );
};
